import { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Box, Container, Breadcrumbs, Link, Typography, Autocomplete, TextField, Tooltip } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FiltersTable from './FiltersTable';
import MenusCreateEdit from './MenusCreateEdit';
import SubMenusCreateEdit from './SubMenusCreateEdit';
import FilterCreateEdit from './FilterCreateEdit';
import AddExistingMenu from './AddExistingMenu';
import AddExistingSubMenu from './AddExistingSubMenu';
import { GetMobileUserGroups } from '../MobileUserGroups/ConnectionService';
import { GetPageFilterList, GetPageGroupKey, GetPageGroupsPagesTree, GetPageByKey } from './ConnectionService';
import AlertMessage from 'src/components/ui-components/AlertMessage';
import { Helmet } from 'react-helmet-async';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  treeItem: {
    '&:hover > $content': {
      backgroundColor: 'linear-gradient(to bottom, #065749, #065749) !important',
    },
    '&:focus > $content, &$selected > $content': {
      backgroundColor: 'linear-gradient(to bottom, #09735e, #09735e) !important',
    },
  },
  content: {
    padding: '5px 8px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'linear-gradient(to bottom, #065749, #065749) !important',
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: 'linear-gradient(to bottom, #09735e, #09735e) !important',
      fontWeight: 'bold !important',
    },
  },
  selected: {},
  label: {
    fontWeight: 'normal',
    '&.Mui-selected': {
      fontWeight: 'bold !important',
    },
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  labelText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '125px',
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 'none',
    },
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

const MenusTree = () => {
  const classes = useStyles();
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);
  const [filters, setFilters] = useState([]);
  const [showFilterCreateEdit, setShowFilterCreateEdit] = useState(false);
  const [isCreatingNewMenu, setIsCreatingNewMenu] = useState(false);
  const [isCreatingNewSubMenu, setIsCreatingNewSubMenu] = useState(false);
  const [userGroupData, setUserGroupData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [menuDetails, setMenuDetails] = useState(null);
  const [subMenuDetails, setSubMenuDetails] = useState(null);
  const [userGroupKey, setUserGroupKey] = useState(null);
  const [selectedMenuPageGroupKey, setSelectedMenuPageGroupKey] = useState(null);

  const [showAddExistingMenu, setShowAddExistingMenu] = useState(false);
  const [showAddExistingSubMenu, setShowAddExistingSubMenu] = useState(false);
  const [newSortOrder, setNewSortOrder] = useState(0);

  // const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // const [confirmDialogData, setConfirmDialogData] = useState({ type: '', id: null });
  const [selected, setSelected] = useState('');
  const [editingFilter, setEditingFilter] = useState(null);

  const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(true);
  const [initialFilters, setInitialFilters] = useState([]);
  const[isUserGroupSelected, setIsUserGroupSelected] = useState(false);
  const [isloading, setIsLoading] = useState<boolean>(false);

  const handleMenuSelect = async (menuId) => {
    const nextSortOrder = getMaxSortOrder(treeData);
    setSelectedMenu(menuId);
    setSelectedSubMenu(null);
    setIsCreatingNewMenu(false);
    setIsCreatingNewSubMenu(false);
    hideAddExistingComponents();
    setIsCheckboxDisabled(true);
    setNewSortOrder(nextSortOrder);

    if (userGroupKey) {
      const res = await GetPageGroupKey(menuId, userGroupKey);
      const menuDetails = res.length > 0 ? res[0] : null;
      setMenuDetails(menuDetails);

      const menuFilters = res.length > 0 ? res[0].pagesFiltersMappings.map(f => f.pageFilterKey) : [];
      await fetchFilters(menuFilters);
    }
  };

  const handleSubMenuSelect = async (subMenuId) => {
    const parentMenuId = findParentMenuId(subMenuId);
    const nextSortOrder = getMaxSortOrder(treeData, parentMenuId);
    setSelectedSubMenu(subMenuId);
    setSelectedMenu(null);
    setIsCreatingNewMenu(false);
    setIsCreatingNewSubMenu(false);
    hideAddExistingComponents();
    setSelectedMenuPageGroupKey(parentMenuId);
    setIsCheckboxDisabled(true);
    setNewSortOrder(nextSortOrder);

    if (userGroupKey) {
      const res = await GetPageByKey(subMenuId, userGroupKey);
      const subMenuDetails = res.length > 0 ? res[0] : null;
      setSubMenuDetails(subMenuDetails);

      const subMenuFilters = res.length > 0 ? res[0].pagesFiltersMappings.map(f => f.pageFilterKey) : [];
      await fetchFilters(subMenuFilters);
    }
  };

  const fetchFilters = async (associatedFilters = []) => {
    const res = await GetPageFilterList();
    const formattedFilters = res.map(filter => ({
      id: filter.pageFilterKey,
      name: filter.pageFilterName,
      type: filter.pageFilterType,
      selected: associatedFilters.includes(filter.pageFilterKey)
    }));
    setFilters(formattedFilters.sort((a, b) => b.selected - a.selected));
    setInitialFilters(formattedFilters.sort((a, b) => b.selected - a.selected));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const updateFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const handleAddFilter = () => {
    setEditingFilter(null);
    setShowFilterCreateEdit(true);
  };

  const handleCancelFilter = () => {
    setShowFilterCreateEdit(false);
    setEditingFilter(null);
  };

  const handleEditFilter = (filter) => {
    setEditingFilter(filter);
    setShowFilterCreateEdit(true);
  };

  const handleToggleFilter = (id) => {
    setFilters(filters.map(filter => filter.id === id ? { ...filter, selected: !filter.selected } : filter));
  };

  const handleSaveFilter = async (savedFilter) => {
    if (editingFilter) {
      setFilters(filters.map(filter => filter.id === savedFilter.pageFilterKey ? { ...filter, ...savedFilter } : filter));
    } else {
      setFilters([...filters, { ...savedFilter, id: savedFilter.pageFilterKey, selected: false }]);
    }
    setShowFilterCreateEdit(false);
    await fetchFilters();
  };

  const handleSelect = (event, nodeIds) => {
    const node = findNodeById(nodeIds);
    if (node.type === 'menu') {
      handleMenuSelect(node.id);
    } else {
      handleSubMenuSelect(node.id);
    }
    setSelected(nodeIds);
  };

  const findNodeById = (id, nodes = treeData) => {
    for (let node of nodes) {
      if (node.id.toString() === id) {
        return node;
      }
      if (node.children) {
        const childNode = findNodeById(id, node.children);
        if (childNode) {
          return childNode;
        }
      }
    }
    return null;
  };

  const getMobileUserGroupsData = async () => {
    const res = await GetMobileUserGroups();
    setUserGroupData(res);
  };

  const getMaxSortOrder = (data, pageGroupKey = null) => {
    if (pageGroupKey) {
      const menu = data.find(item => item.id === pageGroupKey);
      if (menu && menu.children) {
        return menu.children.length + 1;
      }
      return 1;
    } else {
      return data.length + 1;
    }
  };

  // const handleAddNewMenu = () => {
  //   const nextSortOrder = getMaxSortOrder(treeData);
  //   setIsCreatingNewMenu(true);
  //   setSelectedMenu(null);
  //   setSelectedSubMenu(null);
  //   setMenuDetails(null);
  //   setNewSortOrder(nextSortOrder);
  //   hideAddExistingComponents();
  //   setShowAddExistingMenu(true);
  //   setIsCheckboxDisabled(false)
  // };

  // const handleAddNewSubMenu = async (menuId, pageGroupKey) => {
  //   const nextSortOrder = getMaxSortOrder(treeData, pageGroupKey);
  //   await handleMenuSelect(menuId);
  //   setIsCreatingNewSubMenu(true);
  //   setSelectedMenuPageGroupKey(pageGroupKey);
  //   setNewSortOrder(nextSortOrder);
  //   hideAddExistingComponents();
  //   setShowAddExistingSubMenu(true);
  //   setIsCheckboxDisabled(false)
  // };

  const handleCancelAddExistingMenu = () => {
    setShowAddExistingMenu(false);
  };

  const handleSaveAddExistingMenu = async (menuId) => {
    try {
      setShowAddExistingMenu(false);
      await reloadAndFormatTreeData(userGroupKey);
      AlertMessage({ type: 'success', msg: 'Menu Created Successfully' });
      handleMenuSelect(menuId);
    } catch (err) {
      Object.entries(err.errors).forEach(([, errMsg]: any) => {
        AlertMessage({ type: 'error', msg: errMsg.errorMessage });
      });
    }
  };

  const handleCancelAddExistingSubMenu = () => {
    setShowAddExistingSubMenu(false);
  };

  const handleSaveAddExistingSubMenu = async (subMenuId) => {
    try {
      setShowAddExistingSubMenu(false);
      await reloadAndFormatTreeData(userGroupKey);
      AlertMessage({ type: 'success', msg: 'Sub Menu Created Successfully' });
      handleSubMenuSelect(subMenuId);
    } catch (err) {
      Object.entries(err.errors).forEach(([, errMsg]: any) => {
        AlertMessage({ type: 'error', msg: errMsg.errorMessage });
      });
    }
  };

  const hideAddExistingComponents = () => {
    setShowAddExistingMenu(false);
    setShowAddExistingSubMenu(false);
  };

  const checkIfMenuHasSubMenus = (menuId) => {
    const menu = findNodeById(menuId);
    return menu && menu.children && menu.children.length > 0;
  };

  useEffect(() => {
    getMobileUserGroupsData();
    setIsLoading(true);
    fetchFilters();
    setIsLoading(false);
  }, []);

  const reloadAndFormatTreeData = async (userGroupKey) => {
    const res = await GetPageGroupsPagesTree(userGroupKey);
    const formattedData = formatTreeData(res);
    setTreeData(formattedData);
  };

  const handleUserGroupChange = async (event, value) => {
    if (value && value.UserGroupKey) {
      setUserGroupKey(value.UserGroupKey);
      reloadAndFormatTreeData(value.UserGroupKey);
      setIsUserGroupSelected(true);
    } else {
      setUserGroupKey(null);
      setTreeData([]);
      setIsUserGroupSelected(false);
    }
    setSelectedMenu(null);
    setSelectedSubMenu(null);
    setIsCreatingNewMenu(false);
    setIsCreatingNewSubMenu(false);
    setShowAddExistingMenu(false);
    setShowAddExistingSubMenu(false);
  };

  const formatTreeData = (data) => {
    const menuMap = {};
    const tree = [];

    data.forEach((item) => {
      const { pageGroupKey, pageGroupName, pageGroupSortOrder, pageKey, pageName, pageSortOrder } = item;

      if (!menuMap[pageGroupKey]) {
        menuMap[pageGroupKey] = {
          id: pageGroupKey,
          name: pageGroupName,
          sortOrder: pageGroupSortOrder,
          type: 'menu',
          children: [],
        };
        tree.push(menuMap[pageGroupKey]);
      }

      if (pageKey) {
        menuMap[pageGroupKey].children.push({
          id: pageKey,
          name: pageName,
          sortOrder: pageSortOrder,
          type: 'submenu',
        });
      }
    });

    return tree;
  };

  const findParentMenuId = (subMenuId) => {
    for (let node of treeData) {
      if (node.children) {
        for (let child of node.children) {
          if (child.id === subMenuId) {
            return node.id;
          }
        }
      }
    }
    return null;
  };

  // const handleDeleteMenu = (menuId) => {
  //   setConfirmDialogData({ type: 'menu', id: menuId });
  //   setConfirmDialogOpen(true);
  // };

  // const handleDeleteSubMenu = (subMenuId) => {
  //   const parentMenuId = findParentMenuId(subMenuId);
  //   setSelectedMenuPageGroupKey(parentMenuId);
  //   setConfirmDialogData({ type: 'submenu', id: subMenuId });
  //   setConfirmDialogOpen(true);
  // };

  // const handleConfirmDelete = async () => { 
  //   const { type, id } = confirmDialogData;
  //   try {
  //     if (type === 'menu') {
  //       await DeleteUserGroupPageGroupMapping(id, userGroupKey);
  //       AlertMessage({ type: 'success', msg: 'Page Group deleted successfully' });
  //     } else if (type === 'submenu') {
  //       await DeletePageGroupPageMapping(id, selectedMenuPageGroupKey, userGroupKey);
  //       AlertMessage({ type: 'success', msg: 'Page deleted successfully' });
  //     }
  //     await reloadAndFormatTreeData(userGroupKey);
  //   } catch (err) {
  //     Object.entries(err.errors).forEach(([, errMsg]: any) => {
  //       AlertMessage({ type: 'error', msg: errMsg.errorMessage });
  //     });
  //   }
  //   const nextSortOrder = getMaxSortOrder(treeData);
  //   setNewSortOrder(nextSortOrder);
  //   setConfirmDialogOpen(false);
  //   setSelectedMenu(null);
  //   setSelectedSubMenu(null);
  //   setIsCreatingNewMenu(false);
  //   setIsCreatingNewSubMenu(false);
  //   setShowAddExistingMenu(false);
  //   setShowAddExistingSubMenu(false);
  //   fetchFilters();
  // };

  const renderTree = (nodes) => (
    <TreeItem
      key={nodes.id}
      nodeId={nodes.id.toString()}
      label={
        <div className={classes.labelContainer}>
          <Tooltip title={nodes.name}>
            <span className={classes.labelText}>{nodes.name}</span>
          </Tooltip>
          {/* <div className={classes.iconContainer}>
            {nodes.type === 'menu' ? (
              <>
                <Tooltip title="Create SubMenu">
                  <IconButton
                    sx={{ color: "#072f40" }}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleAddNewSubMenu(nodes.id, nodes.id);
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Menu">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDeleteMenu(nodes.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Delete SubMenu">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDeleteSubMenu(nodes.id);
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            )}
          </div> */}
        </div>
      }
      classes={{
        root: classes.treeItem,
        content: classes.content,
        selected: classes.selected,
        label: classes.label,
      }}
      onClick={() => handleSelect(null, nodes.id.toString())}
    >
      {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
    </TreeItem>
  );  

  return (
    <>
      <Helmet>
        <title>Menu Configuration</title>
      </Helmet>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Grid
            alignItems="center"
            sx={{ my: 2 }}
            container
            justifyContent="space-between"
          >
            <Grid item>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
              >
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to=""
                  variant="h5"
                >
                  Configurations
                </Link>
                <Typography color="textPrimary" variant="h5">
                  Menus
                </Typography>
              </Breadcrumbs>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xl={3} lg={3} md={12} sm={12} xs={12}>
              <Card style={{ height: '100%' }}>
                <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" sx={{ my: 2 }}>Select User Group</Typography>
                  <Autocomplete
                    options={userGroupData}
                    getOptionLabel={(option) => option.userGroup}
                    renderInput={(params) => <TextField {...params} label="User Group" variant="outlined" />}
                    style={{ marginBottom: '16px' }}
                    onChange={handleUserGroupChange}
                  />
                  {isUserGroupSelected && (
                    <>
                      <Typography color="textPrimary" variant="h5" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Menus
                        {/* <Tooltip title="Create Menu">
                          <IconButton sx={{ color: "#072f40" }} size="small" onClick={handleAddNewMenu}>
                            <AddBoxIcon />
                          </IconButton>
                        </Tooltip> */}
                      </Typography>
                      <span>
                        <TreeView
                          className={classes.root}
                          defaultCollapseIcon={<ExpandMoreIcon />}
                          defaultExpandIcon={<ChevronRightIcon />}
                          selected={selected}
                          onNodeSelect={handleSelect}
                        >
                          {treeData.map((node) => renderTree(node))}
                        </TreeView>
                      </span>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xl={9} lg={9} md={12} sm={12} xs={12}>
              <Card>
                <CardContent>
                  {showAddExistingMenu ? (
                    <AddExistingMenu
                      onCancel={handleCancelAddExistingMenu}
                      userGroupKey={userGroupKey}
                      onSave={handleSaveAddExistingMenu}
                    />
                  ) : showAddExistingSubMenu ? (
                    <AddExistingSubMenu
                      onCancel={handleCancelAddExistingSubMenu}
                      userGroupKey={userGroupKey}
                      onSave={handleSaveAddExistingSubMenu}
                    />
                  ) : isCreatingNewMenu ? (
                    <MenusCreateEdit
                      selectedMenu={null}
                      menuDetails={null}
                      userGroupKey={userGroupKey}
                      newSortOrder={newSortOrder}
                      reloadTree={reloadAndFormatTreeData}
                      setIsCheckboxDisabled={setIsCheckboxDisabled}
                      resetFilters={resetFilters}
                      handleMenuSelect={handleMenuSelect}
                      filters={filters}
                      updateFilters={updateFilters}
                      checkIfMenuHasSubMenus={checkIfMenuHasSubMenus}
                    />
                  ) : isCreatingNewSubMenu ? (
                    <SubMenusCreateEdit
                      selectedMenu={selectedSubMenu}
                      subMenuDetails={subMenuDetails}
                      userGroupKey={userGroupKey}
                      pageGroupKey={selectedMenuPageGroupKey}
                      newSortOrder={newSortOrder}
                      reloadTree={reloadAndFormatTreeData}
                      setIsCheckboxDisabled={setIsCheckboxDisabled}
                      resetFilters={resetFilters}
                      handleSubMenuSelect={handleSubMenuSelect}
                      filters={filters}
                      updateFilters={updateFilters}
                    />
                  ) : selectedMenu ? (
                    <MenusCreateEdit
                      selectedMenu={selectedMenu}
                      menuDetails={menuDetails}
                      userGroupKey={userGroupKey}
                      newSortOrder={newSortOrder}
                      reloadTree={reloadAndFormatTreeData}
                      setIsCheckboxDisabled={setIsCheckboxDisabled}
                      resetFilters={resetFilters}
                      handleMenuSelect={handleMenuSelect}
                      filters={filters}
                      updateFilters={updateFilters}
                      checkIfMenuHasSubMenus={checkIfMenuHasSubMenus}
                    />
                  ) : selectedSubMenu ? (
                    <SubMenusCreateEdit
                      selectedMenu={selectedSubMenu}
                      subMenuDetails={subMenuDetails}
                      userGroupKey={userGroupKey}
                      newSortOrder={newSortOrder}
                      reloadTree={reloadAndFormatTreeData}
                      setIsCheckboxDisabled={setIsCheckboxDisabled}
                      resetFilters={resetFilters}
                      handleSubMenuSelect={handleSubMenuSelect}
                      filters={filters}
                      updateFilters={updateFilters}
                    />
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="h5" color="textSecondary">
                        Please select Menu or Sub Menu
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              <Card style={{ marginTop: '8px' }}>
                <CardContent>
                  {showFilterCreateEdit ? (
                    <FilterCreateEdit
                      filter={editingFilter}
                      onClickCancel={handleCancelFilter}
                      onSaveFilter={handleSaveFilter}
                    />
                  ) : (
                    <FiltersTable
                      filters={filters}
                      onAddFilter={handleAddFilter}
                      onClickEditFilter={handleEditFilter}
                      onToggleFilter={handleToggleFilter}
                      isCheckboxDisabled={isCheckboxDisabled}
                      isLoading={isloading}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
        {/* <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this {confirmDialogData.type}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined" color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="primary">
              Delete
            </Button>
          </DialogActions>
        </Dialog> */}
      </Box>
    </>
  );
};

export default MenusTree;
